export type AnomalyStatus = 'NEW' | 'SEEN' | 'ACKNOWLEDGED' | 'DISMISSED';
export type AnomalySeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type AnomalyDirection = 'UP' | 'DOWN';
export type Timeframe = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';

export interface Anomaly {
  id: number;
  rule_id: number;
  table_name: string;
  timeframe: Timeframe;
  current_period_start: string;
  current_period_end: string;
  prior_period_start: string;
  prior_period_end: string;
  current_value: number | null;
  prior_value: number | null;
  delta: number | null;
  delta_pct: number | null;
  /** What this alert MEANS, decided once on the server so the card, the alert
   *  title and the context sent to the model cannot disagree with each other.
   *  `pct` is null wherever a percentage would say nothing — a balance around
   *  zero, or a baseline that is floating-point dust. Optional because an
   *  older platform build does not send it; the card falls back to the raw
   *  fields, which is exactly today's behaviour. */
  presentation?: {
    semantics: 'standard' | 'negative_magnitude' | 'signed_balance';
    verb: string;
    headline: string;
    pct: number | null;
    arrow: 'up' | 'down' | 'flat';
    display_prior: number | null;
    display_current: number | null;
    absolute_change: number | null;
    /** What the movement is WORTH, in both languages, rendered server-side.
     *  `scale_known` is false when the metric has no stable monthly scale to
     *  measure against — a trial-balance movement that nets to zero. The
     *  sentence then says the size is unknown, and `share_of_month` and
     *  `per_day` are null. Never fill those in here: a number invented on the
     *  client is the exact failure this field exists to prevent. */
    impact?: {
      amount: number;
      unit: string | null;
      aggregate: string;
      /** An average or a percentage — a LEVEL, not a total. It scales
       *  differently and it cannot state its own cost without a volume. */
      is_rate: boolean;
      scale_known: boolean;
      share_of_month: number | null;
      above_usual: number | null;
      monthly_total: number | null;
      threshold_pct: number | null;
      /** What a rate movement cost over the period, once the volume it applies
       *  to is known. Null when the rule declares no volume — which reads as
       *  "no cost can be stated", never as zero. */
      cost: number | null;
      /** The unit `cost` is in — the VOLUME's unit, not the metric's. 1.5
       *  points against 3,920 tonnes is tonnes; against 11.2M SAR is SAR. */
      cost_unit: string | null;
      rate_basis: string;
      per_day: number | null;
      timeframe: string;
      /** Which group this finding falls in, from the executor's own
       *  materiality cut-offs. `unmeasured` is its own band, not the bottom. */
      band: string;
      band_label: { en: string; ar: string };
      sentence: { en: string; ar: string };
    };
    /** What the figure is compared WITH. `yoy_calendar` = the same calendar
     *  dates one year earlier; `notes` say when either side was Eid or
     *  Ramadan. Optional: an older platform build does not send it, and the
     *  card then falls back to the timeframe wording. */
    comparison?: {
      basis: 'previous_period' | 'yoy_calendar';
      label: string;
      compared_with: string;
      notes: string[];
    };
  };
  direction: AnomalyDirection;
  severity: AnomalySeverity;
  status: AnomalyStatus;
  reviewer: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  detected_at: string;
  context: Record<string, unknown> | null;
  rule?: {
    metric_name: string | null;
    metric_sql: string | null;
    rule_type: string | null;
    notes: string | null;
  };
}

export interface AnomalyList {
  items: Anomaly[];
  next_cursor: string | null;
}

export interface AnomalySummary {
  /** What the owner's weekly page actually shows — the badge reads this. */
  owner_open_count?: number;
  new_count: number;
  seen_count: number;
  ack_count: number;
  dismissed_count: number;
  open_count: number;
  resolved_count: number;
  critical_open_count: number;
  high_open_count: number;
  total: number;
  last_detected_at: string | null;
}

interface ListFilters {
  /** Single status or comma-separated CSV (e.g. "NEW,SEEN") — backend splits. */
  status?: string;
  timeframe?: Timeframe;
  severity?: AnomalySeverity;
  table_name?: string;
  limit?: number;
  cursor?: string;
  /** 'recent' (default) or 'impact'. Ordering is the server's job: the list is
   *  keyset-paginated, so sorting a page here lets a LOW from page 1 outrank a
   *  CRITICAL from page 2. Cursors are tagged per sort and are not
   *  interchangeable. */
  sort?: 'recent' | 'impact';
}

async function jsonFetch(url: string, init?: RequestInit) {
  const res = await fetch(url, init);
  if (!res.ok) {
    const err = await res
      .json()
      .catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function apiListAnomalies(
  filters: ListFilters = {}
): Promise<AnomalyList> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.timeframe) qs.set('timeframe', filters.timeframe);
  if (filters.severity) qs.set('severity', filters.severity);
  if (filters.table_name) qs.set('table_name', filters.table_name);
  if (filters.limit) qs.set('limit', String(filters.limit));
  if (filters.cursor) qs.set('cursor', filters.cursor);
  if (filters.sort) qs.set('sort', filters.sort);
  return jsonFetch(`/api/anomalies?${qs.toString()}`);
}

/** One week of the owner's view: the cards that cleared the money floor. */
export interface AnomalyWeek {
  week_start: string;
  week_end: string;
  /** The week's biggest move per metric; `others` are the smaller ones it leads. */
  items: (Anomaly & { others?: Anomaly[] })[];
}

export interface AnomalyWeekly {
  weeks: AnomalyWeek[];
  /** What this customer moves in a day, from the metrics it already tracks. */
  daily_scale: number | null;
  /** The smallest movement that earns a place here. Null when scale is unknown. */
  floor: number | null;
  hidden_below_floor: number;
  folded_into_weeks: number;
}

export async function apiWeeklyAnomalies(weeks = 8): Promise<AnomalyWeekly> {
  return jsonFetch(`/api/anomalies/weekly?weeks=${weeks}`);
}

export async function apiGetAnomaly(id: number): Promise<Anomaly> {
  return jsonFetch(`/api/anomalies/${id}`);
}

export async function apiPatchAnomaly(
  id: number,
  body: { status?: AnomalyStatus; review_notes?: string }
): Promise<{ ok: boolean; id: number }> {
  return jsonFetch(`/api/anomalies/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
}

export async function apiAnomalySummary(): Promise<AnomalySummary> {
  return jsonFetch('/api/anomalies/summary');
}

export async function apiTriggerAnomalyGeneration(): Promise<{
  customer_pk: number;
  rules_persisted: number;
}> {
  return jsonFetch('/api/anomaly-rules/generate', { method: 'POST' });
}

export async function apiTriggerAnomalyRun(): Promise<{
  anomalies_created: number;
}> {
  return jsonFetch('/api/internal/anomaly-run', { method: 'POST' });
}

// ── Rules ─────────────────────────────────────────────────────────────────────

export type RuleType = 'PCT_CHANGE' | 'SAME_WEEKDAY_ZSCORE';
export type RuleDirection = 'UP' | 'DOWN' | 'BOTH';

export interface AnomalyRule {
  id: number;
  table_name: string;
  is_json: boolean;
  rule_type: RuleType;
  metric_name: string;
  metric_sql: string;
  date_column: string;
  timeframes: Timeframe[];
  direction: RuleDirection;
  severity: AnomalySeverity;
  enabled: boolean;
  config: Record<string, unknown>;
  notes: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export type RuleCreateBody = Omit<
  AnomalyRule,
  'id' | 'enabled' | 'created_by' | 'created_at' | 'updated_at'
>;

export type RulePatchBody = Partial<{
  metric_name: string;
  metric_sql: string;
  date_column: string;
  timeframes: Timeframe[];
  direction: RuleDirection;
  severity: AnomalySeverity;
  enabled: boolean;
  config: Record<string, unknown>;
  notes: string | null;
}>;

export async function apiListRules(
  includeDisabled = true
): Promise<{ items: AnomalyRule[] }> {
  const qs = includeDisabled ? '?include_disabled=true' : '';
  return jsonFetch(`/api/anomaly-rules${qs}`);
}

export async function apiCreateRule(
  body: RuleCreateBody
): Promise<{ id: number }> {
  return jsonFetch('/api/anomaly-rules', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
}

export async function apiUpdateRule(
  id: number,
  patch: RulePatchBody
): Promise<{ ok: boolean; id: number }> {
  return jsonFetch(`/api/anomaly-rules/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch)
  });
}

export async function apiDeleteRule(
  id: number
): Promise<{ ok: boolean; id: number }> {
  return jsonFetch(`/api/anomaly-rules/${id}`, { method: 'DELETE' });
}
